import { useState } from "react";
import { eur, hours, monthLabel } from "@/lib/format";
import type { MonthPoint } from "@/lib/types";
import { cn } from "@/lib/utils";
import { monthShort, niceMax, ticks } from "./chart";

const SERIES = [
  { key: "invoicedEur", label: "Verrechnet", color: "bg-chart-1", stroke: "var(--chart-1)" },
  { key: "openEur", label: "Offen", color: "bg-chart-2", stroke: "var(--chart-2)" },
] as const;

const axisEur = (n: number) => `${new Intl.NumberFormat("de-DE").format(n)} €`;

/**
 * Billable amount per month, stacked verrechnet (bottom) / offen (top).
 * Columns are buttons: hover or focus shows the readout, click selects the month.
 */
export function MonthlyChart({ months, selected, onSelect }: { months: MonthPoint[]; selected: string; onSelect: (m: string) => void }) {
  const [active, setActive] = useState<number | null>(null);
  const max = niceMax(Math.max(...months.map((m) => m.amountEur)));
  const grid = ticks(max);
  const pct = (v: number) => `${(v / max) * 100}%`;
  const shown = active !== null ? months[active] : null;

  return (
    <figure className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <figcaption id="chart-title" className="text-base font-semibold">Umsatz je Monat</figcaption>
        <ul className="flex gap-4 text-sm text-muted-foreground" aria-label="Legende">
          {SERIES.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5"><span aria-hidden className={cn("size-2.5 rounded-[3px]", s.color)} />{s.label}</li>
          ))}
        </ul>
      </div>

      <div className="-mx-4 overflow-x-auto px-4">
        <div className="relative min-w-[36rem]">
          {/* Plot */}
          <div className="relative mt-3 ml-14 h-56" onPointerLeave={() => setActive(null)}>
            {grid.map((t) => (
              <div key={t} aria-hidden className="absolute inset-x-0 border-t border-border" style={{ bottom: pct(t) }}>
                <span className="absolute right-full mr-2 -translate-y-1/2 text-xs text-muted-foreground tabular-nums">{axisEur(t)}</span>
              </div>
            ))}
            <div className="absolute inset-0 flex" role="group" aria-labelledby="chart-title">
              {months.map((m, i) => {
                const isSel = m.month === selected;
                const both = m.invoicedEur > 0 && m.openEur > 0;
                return (
                  <button
                    key={m.month}
                    type="button"
                    aria-pressed={isSel}
                    aria-label={`${monthLabel(m.month)}: ${eur(m.amountEur)}, davon ${eur(m.invoicedEur)} verrechnet und ${eur(m.openEur)} offen`}
                    onPointerEnter={() => setActive(i)}
                    onFocus={() => setActive(i)}
                    onBlur={() => setActive(null)}
                    onClick={() => onSelect(m.month)}
                    className={cn(
                      "group relative flex flex-1 cursor-pointer flex-col items-center justify-end rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50",
                      isSel ? "bg-muted" : "hover:bg-muted/50",
                    )}
                  >
                    {/* One column: offen stacked on verrechnet, 2px surface gap between, rounded data-end only. */}
                    <div className={cn("flex w-6 flex-col justify-end", both && "gap-[2px]")} style={{ height: pct(m.amountEur) }}>
                      {m.openEur > 0 && <div className="rounded-t-[4px] bg-chart-2" style={{ flexGrow: m.openEur, flexBasis: 0 }} />}
                      {m.invoicedEur > 0 && <div className={cn("bg-chart-1", m.openEur === 0 && "rounded-t-[4px]")} style={{ flexGrow: m.invoicedEur, flexBasis: 0 }} />}
                    </div>
                  </button>
                );
              })}
            </div>

            {shown && active !== null && (
              <div
                role="status"
                // Beside the column, pinned to the top of the plot: never clipped by the scroll
                // container and never covering the hovered bar. Flips to the left on the right half.
                className="pointer-events-none absolute top-0 z-10 min-w-44 rounded-lg whitespace-nowrap bg-popover p-2.5 text-sm text-popover-foreground shadow-md ring-1 ring-border"
                style={active < months.length / 2
                  ? { left: `calc(${((active + 1) / months.length) * 100}% + 0.25rem)` }
                  : { right: `calc(${((months.length - active) / months.length) * 100}% + 0.25rem)` }}
              >
                <p className="mb-1.5 text-xs text-muted-foreground">{monthLabel(shown.month)} · {hours(shown.billableMinutes)}</p>
                {SERIES.map((s) => (
                  <p key={s.key} className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-1.5 text-muted-foreground"><span aria-hidden className="h-0.5 w-3 rounded-full" style={{ background: s.stroke }} />{s.label}</span>
                    <span className="font-medium tabular-nums">{eur(shown[s.key])}</span>
                  </p>
                ))}
              </div>
            )}
          </div>

          {/* Month labels; the year appears on January and on the first column. */}
          <div aria-hidden className="ml-14 flex pt-2">
            {months.map((m, i) => (
              <span key={m.month} className={cn("flex-1 text-center text-xs tabular-nums", m.month === selected ? "text-foreground" : "text-muted-foreground")}>
                {monthShort(m.month)}
                {(i === 0 || m.month.endsWith("-01")) && <span className="block text-[0.7rem] text-muted-foreground">{m.month.slice(0, 4)}</span>}
              </span>
            ))}
          </div>
        </div>
      </div>

      <table className="sr-only">
        <caption>Umsatz je Monat</caption>
        <thead><tr><th>Monat</th><th>Stunden</th><th>Verrechnet</th><th>Offen</th><th>Gesamt</th></tr></thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}><td>{monthLabel(m.month)}</td><td>{hours(m.billableMinutes)}</td><td>{eur(m.invoicedEur)}</td><td>{eur(m.openEur)}</td><td>{eur(m.amountEur)}</td></tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}
